import { getAddressFieldsConfig } from 'app/pages/loanApplication/LoanApplication.sharedConfigs';
import { getContactsConfig } from 'app/pages/loanApplication/tabs/propertyAndLoan/EditModal/Content/MainContent/Contacts/Contacts.config';
import { loanInfoConfigFields } from 'app/pages/loanApplication/tabs/propertyAndLoan/EditModal/Content/MainContent/LoanInfo/LoanInfo.config';
import { projectConfigFields } from 'app/pages/loanApplication/tabs/propertyAndLoan/EditModal/Content/MainContent/Project/Project.config';
import { propertyConfigFields } from 'app/pages/loanApplication/tabs/propertyAndLoan/EditModal/Content/MainContent/Property/Property.config';
import {
  existingPropertyExpensesFields,
  proposedPropertyExpensesFields,
} from 'app/pages/loanApplication/tabs/propertyAndLoan/EditModal/Content/MainContent/PropertyExpenses/PropertyExpenses.config';
import { transactionDetailsConfigFields } from 'app/pages/loanApplication/tabs/propertyAndLoan/EditModal/Content/MainContent/TransactionDetails/TransactionDetails.config';
import { appFormsTreeFieldOptionsMap } from 'app/services/autoLockForms/autoLockForms';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import { CUSTOM_FIELDS_TARGET } from 'app/shared/components/customFields/CustomFieldsSection/CustomFieldsSection.constants';
import {
  isCalculatedDisplayableInLoanEditor,
  isManualEnabled,
} from 'app/shared/components/customFields/CustomFieldsSection/CustomFieldsSection.utils';
import _ from 'lodash';

import { FieldType } from 'constants/formGenerator';
import {
  LOAN_EDITOR_CUSTOM_FORM_IFRAME_DEFAULT_HEIGHT_PX,
  LoanEditorCustomFormItemKind,
  LoanEditorCustomFormSectionKind,
  LoanEditorCustomFormSectionLayout,
} from 'constants/loanEditorCustomForms';
import { getConditionalArray, getConditionalObject } from 'utils/data';
import { fillConfigFieldsWithKeys } from 'utils/loanManagement';
import { checkIsPendingChangeHasDifference } from 'utils/pendingChanges';

import {
  APPRAISAL_FIELD_NAMES,
  getAppraisalConfigFieldsForContext,
  getAppraisalSeedFields,
} from './LoanEditorCustomFormDrawer.appraisal.utils';
import { getBorrowerConfigFieldsForEntity } from './LoanEditorCustomFormDrawer.borrower.utils';
import {
  CLOSING_INFORMATION_FIELD_NAMES,
  getClosingCompanyConfigFieldsForContext,
  getClosingConfigFieldsForContext,
  getClosingSeedFields,
} from './LoanEditorCustomFormDrawer.closing.utils';
import {
  CLOSING_COMPANY_FIELD_NAMES,
  CLOSING_COMPANY_SELECTOR_FIELD_NAME,
  DATE_PICKER_SELECTOR,
  DEFAULT_COLUMN_COUNT,
  DRAWER_MESSAGES,
  INSURANCE_ESCROWED_FIELD_NAME,
  INVALID_FIELD_SELECTOR,
  LOAN_FIELD_KEY_FORM_INDEX,
  LOAN_FIELD_KEY_MIN_PARTS,
  LOAN_FIELD_KEY_NAME_INDEX,
  POST_CLOSING_FORM_PATH,
} from './LoanEditorCustomFormDrawer.constants';
import {
  checkIsEntityAvailable,
  flattenGroupFields,
} from './LoanEditorCustomFormDrawer.entity.utils';
import {
  checkIsEscrowMasterFieldName,
  getEscrowCascadeFieldPaths,
} from './LoanEditorCustomFormDrawer.escrow.utils';
import {
  FUNDING_FIELD_NAMES,
  getFundingConfigFieldsForContext,
  getFundingSeedFields,
} from './LoanEditorCustomFormDrawer.funding.utils';
import { getLoanInfoGroups } from './LoanEditorCustomFormDrawer.loanAndLock.utils';
import { getDownPaymentCascadeFieldPaths } from './LoanEditorCustomFormDrawer.loanInfoDefaults.utils';
import {
  checkIsMortgageInsuranceOnly,
  checkShouldSeedMortgageInsurance,
  getMortgageInsuranceConfigFieldsForContext,
  getMortgageInsuranceGroups,
  MORTGAGE_INSURANCE_FIELD_NAMES,
} from './LoanEditorCustomFormDrawer.mortgageInsurance.utils';
import {
  getDrawerPostClosingSectionFields,
  getPostClosingConfigFieldsForContext,
  POST_CLOSING_SECTION_KEY_BY_FIELD_NAME,
} from './LoanEditorCustomFormDrawer.postClosing.utils';
import { getPurposeHiddenPropertyFieldIds } from './LoanEditorCustomFormDrawer.property.utils';

export const DrawerEntityKind = {
  LOAN_AND_LOCK: 'LoanAndLock',
  PROPERTY_INFO: 'PropertyInfo',
  BORROWER: 'Borrower',
  CONTACT: 'Contact',
  POST_CLOSING: 'PostClosing',
  CLOSING: 'Closing',
  FUNDING: 'Funding',
  CUSTOM_FIELD: 'CustomField',
};

const DrawerItemResolutionKind = {
  LOAN_FIELD: 'LoanField',
  CUSTOM_FIELD: 'CustomField',
  UNSUPPORTED: 'Unsupported',
  INAPPLICABLE: 'Inapplicable',
};

const COLUMN_COUNT_BY_LAYOUT = {
  [LoanEditorCustomFormSectionLayout.ONE_COLUMN]: 1,
  [LoanEditorCustomFormSectionLayout.TWO_COLUMNS]: 2,
  [LoanEditorCustomFormSectionLayout.THREE_COLUMNS]: 3,
  [LoanEditorCustomFormSectionLayout.FOUR_COLUMNS]: 4,
};

const MIN_ITEM_WIDTH_IN_COLUMNS = 1;

const resolveItemWidthInColumns = item => {
  const width = Number(item?.widthInColumns);

  if (!Number.isFinite(width) || width < MIN_ITEM_WIDTH_IN_COLUMNS) {
    return MIN_ITEM_WIDTH_IN_COLUMNS;
  }

  return Math.floor(width);
};

const SUPPORTED_LOAN_AND_LOCK_FORM_NAMES = new Set([
  FormNames.LOAN_DETAILS,
  FormNames.ADDITIONAL_LOAN_INFO,
  FormNames.DOCUMENT_VERIFICATION_TYPES,
  FormNames.MORTGAGE_INSURANCE,
]);

const FIELD_NAMES_BY_FORM_NAME = {
  [FormNames.MORTGAGE_INSURANCE]: MORTGAGE_INSURANCE_FIELD_NAMES,
  [FormNames.APPRAISAL]: APPRAISAL_FIELD_NAMES,
  [FormNames.CLOSING_INFORMATION]: CLOSING_INFORMATION_FIELD_NAMES,
  [FormNames.CLOSING_COMPANY_INFORMATION]: CLOSING_COMPANY_FIELD_NAMES,
  [FormNames.FUNDING_INFORMATION]: FUNDING_FIELD_NAMES,
};

const SUPPORTED_PROPERTY_INFO_FORM_NAMES = new Set([
  FormNames.PROPERTY_ADDRESS,
  FormNames.PROJECT,
  FormNames.TRANSACTION_DETAILS,
  FormNames.PROPOSED_PROPERTY_EXPENSES,
  FormNames.EXISTING_PROPERTY_EXPENSES,
  FormNames.APPRAISAL,
]);

const SUPPORTED_BORROWER_FORM_NAMES = new Set([FormNames.BORROWER_INFO]);

const SUPPORTED_CONTACT_FORM_NAMES = new Set([FormNames.CONTACTS]);

const SUPPORTED_POST_CLOSING_FORM_NAMES = new Set([FormNames.POST_CLOSING]);

const APPRAISAL_FORM_NAMES = new Set([FormNames.APPRAISAL]);

const SUPPORTED_CLOSING_FORM_NAMES = new Set([
  FormNames.CLOSING_INFORMATION,
  FormNames.CLOSING_COMPANY_INFORMATION,
]);

const SUPPORTED_FUNDING_FORM_NAMES = new Set([FormNames.FUNDING_INFORMATION]);

const CATALOG_LABEL_FORM_NAMES = new Set([FormNames.CLOSING_COMPANY_INFORMATION]);

const PROPERTY_INFO_CONFIG_FIELDS = {
  ...propertyConfigFields,
  ...projectConfigFields,
  ...transactionDetailsConfigFields,
  ...proposedPropertyExpensesFields,
  ...existingPropertyExpensesFields,
};

const getPropertyExpensesSeedFields = () => [
  ...Object.values(proposedPropertyExpensesFields),
  ...Object.values(existingPropertyExpensesFields),
];

const getPropertyAddressSeedFields = ({ county }) => [
  ...Object.values(
    getAddressFieldsConfig({
      prefix: '',
      isWithCountry: false,
      filteredCounties: county ? [{ key: county, value: county, name: county }] : [],
    }),
  ),
  ...Object.values(propertyConfigFields),
];

const PROPERTY_SEED_FIELDS_BY_FORM_NAME = {
  [FormNames.PROPERTY_ADDRESS]: getPropertyAddressSeedFields,
  [FormNames.PROJECT]: () => Object.values(projectConfigFields),
  [FormNames.TRANSACTION_DETAILS]: () => Object.values(transactionDetailsConfigFields),
  [FormNames.PROPOSED_PROPERTY_EXPENSES]: getPropertyExpensesSeedFields,
  [FormNames.EXISTING_PROPERTY_EXPENSES]: getPropertyExpensesSeedFields,
  [FormNames.APPRAISAL]: getAppraisalSeedFields,
};

const ENTITY_KIND_BY_FORM_NAME = new Map([
  ...[...SUPPORTED_LOAN_AND_LOCK_FORM_NAMES].map(formName => [
    formName,
    DrawerEntityKind.LOAN_AND_LOCK,
  ]),
  ...[...SUPPORTED_PROPERTY_INFO_FORM_NAMES].map(formName => [
    formName,
    DrawerEntityKind.PROPERTY_INFO,
  ]),
  ...[...SUPPORTED_BORROWER_FORM_NAMES].map(formName => [formName, DrawerEntityKind.BORROWER]),
  ...[...SUPPORTED_CONTACT_FORM_NAMES].map(formName => [formName, DrawerEntityKind.CONTACT]),
  ...[...SUPPORTED_POST_CLOSING_FORM_NAMES].map(formName => [
    formName,
    DrawerEntityKind.POST_CLOSING,
  ]),
  ...[...SUPPORTED_CLOSING_FORM_NAMES].map(formName => [formName, DrawerEntityKind.CLOSING]),
  ...[...SUPPORTED_FUNDING_FORM_NAMES].map(formName => [formName, DrawerEntityKind.FUNDING]),
]);

const CONFIG_FIELDS_BY_ENTITY_KIND = {
  [DrawerEntityKind.LOAN_AND_LOCK]: loanInfoConfigFields,
  [DrawerEntityKind.PROPERTY_INFO]: PROPERTY_INFO_CONFIG_FIELDS,
};

const PER_RECORD_ENTITY_KINDS = new Set([DrawerEntityKind.BORROWER, DrawerEntityKind.CONTACT]);

const getContactConfigGroupsForEntity = ({ contactId, contactTypesOptions }) =>
  getContactsConfig({
    contactId,
    contactTypesOptions: contactTypesOptions ?? [],
    numberOfColumns: DEFAULT_COLUMN_COUNT,
    isEmail: true,
    isPhoneNumber: true,
  }).groups;

const getContactConfigFieldsForEntity = ({ contactId, contactTypesOptions }) =>
  getContactConfigGroupsForEntity({ contactId, contactTypesOptions }).reduce((acc, group) => {
    group.fields.forEach(field => {
      if (field?.fieldId) {
        acc[field.fieldId] = field;
      }
    });

    return acc;
  }, {});

export const buildContactSelectorOptions = contacts =>
  (contacts ?? []).map(contact => {
    const personName = [contact.firstName, contact.lastName].filter(Boolean).join(' ').trim();
    const fullName = personName || contact.companyName || contact.id;

    return {
      id: contact.id,
      fullName,
    };
  });

const PER_RECORD_FIELD_PROBE_ENTITY_ID = '__probe__';

const getLoanAndLockConfigFieldsForContext = loanAndLockContext =>
  loanAndLockContext?.isEnabled ? flattenGroupFields(getLoanInfoGroups(loanAndLockContext)) : [];

const LOAN_AND_LOCK_MULTI_FIELD_IDS = new Set([
  INSURANCE_ESCROWED_FIELD_NAME,
  loanInfoConfigFields.housingFinanceAgencyId.fieldId,
]);

const findLoanAndLockConfigField = ({ loanAndLockConfigFields, fieldName }) => {
  if (LOAN_AND_LOCK_MULTI_FIELD_IDS.has(fieldName)) {
    return _.filter(loanAndLockConfigFields, { fieldId: fieldName });
  }

  const nameInModel = loanInfoConfigFields[fieldName]?.nameInModel;

  return (
    _.find(loanAndLockConfigFields, { nameInModel }) ??
    _.find(loanAndLockConfigFields, { fieldId: fieldName })
  );
};

const resolveLoanAndLockBaseFieldConfig = ({
  formName,
  fieldName,
  mortgageInsuranceContext,
  loanAndLockConfigFields,
}) =>
  formName === FormNames.MORTGAGE_INSURANCE
    ? getMortgageInsuranceConfigFieldsForContext(mortgageInsuranceContext)[fieldName]
    : findLoanAndLockConfigField({ loanAndLockConfigFields, fieldName });

const PER_RECORD_SUPPORTED_FIELD_NAMES = {
  [DrawerEntityKind.BORROWER]: new Set(
    Object.keys(getBorrowerConfigFieldsForEntity({ entityId: PER_RECORD_FIELD_PROBE_ENTITY_ID })),
  ),
  [DrawerEntityKind.CONTACT]: new Set(
    Object.keys(getContactConfigFieldsForEntity({ contactId: PER_RECORD_FIELD_PROBE_ENTITY_ID })),
  ),
};

const getPerRecordEntityFieldConfigName = ({ entityKind, fieldName }) =>
  PER_RECORD_SUPPORTED_FIELD_NAMES[entityKind]?.has(fieldName) ? fieldName : null;

const checkIsFieldSupportedByEntity = ({ entityKind, formName, fieldName }) => {
  if (_.has(FIELD_NAMES_BY_FORM_NAME, formName)) {
    return FIELD_NAMES_BY_FORM_NAME[formName].has(fieldName);
  }

  if (PER_RECORD_ENTITY_KINDS.has(entityKind)) {
    return !_.isNil(getPerRecordEntityFieldConfigName({ entityKind, fieldName }));
  }

  if (entityKind === DrawerEntityKind.POST_CLOSING) {
    return _.has(POST_CLOSING_SECTION_KEY_BY_FIELD_NAME, fieldName);
  }

  if (
    entityKind === DrawerEntityKind.LOAN_AND_LOCK &&
    LOAN_AND_LOCK_MULTI_FIELD_IDS.has(fieldName)
  ) {
    return true;
  }

  return !_.isNil(CONFIG_FIELDS_BY_ENTITY_KIND[entityKind]?.[fieldName]);
};

const parseLoanFieldKey = loanFieldKey => {
  if (_.isNil(loanFieldKey)) {
    return null;
  }

  const parts = String(loanFieldKey).split('-');

  if (parts.length < LOAN_FIELD_KEY_MIN_PARTS) {
    return null;
  }

  return {
    form: parts[LOAN_FIELD_KEY_FORM_INDEX],
    name: parts.slice(LOAN_FIELD_KEY_NAME_INDEX).join('-'),
  };
};

export const checkIsLoanFieldKeySupported = loanFieldKey => {
  const parsed = parseLoanFieldKey(loanFieldKey);
  const entityKind = parsed ? ENTITY_KIND_BY_FORM_NAME.get(parsed.form) : null;

  if (_.isNil(entityKind)) {
    return false;
  }

  return checkIsFieldSupportedByEntity({
    entityKind,
    formName: parsed.form,
    fieldName: parsed.name,
  });
};

const resolveLoanFieldLabel = ({ item, parsed }) => {
  const overridden = item?.labelOverride?.trim();

  if (overridden) {
    return overridden;
  }

  if (item?.loanFieldName) {
    return item.loanFieldName;
  }

  const pickerOption = appFormsTreeFieldOptionsMap?.[item?.loanFieldKey];

  if (pickerOption?.name) {
    return pickerOption.name;
  }

  if (parsed?.name) {
    return parsed.name;
  }

  return item?.loanFieldKey ?? DRAWER_MESSAGES.UNSUPPORTED_FALLBACK_LABEL;
};

const resolveCustomFieldLabel = ({ item, definition }) => {
  const overridden = item?.labelOverride?.trim();

  if (overridden) {
    return overridden;
  }

  if (definition?.name) {
    return definition.name;
  }

  if (item?.customFieldName) {
    return item.customFieldName;
  }

  return DRAWER_MESSAGES.UNSUPPORTED_FALLBACK_LABEL;
};

const resolveItemFieldConfigs = ({ baseFieldConfig, label, widthInColumns, isReadOnly }) =>
  Array.isArray(baseFieldConfig)
    ? {
        fieldConfigs: baseFieldConfig.map(fieldConfig =>
          applyItemOverrides({ fieldConfig, widthInColumns, isReadOnly }),
        ),
      }
    : {
        fieldConfig: applyItemOverrides({
          fieldConfig: baseFieldConfig,
          label,
          widthInColumns,
          isReadOnly,
        }),
      };

const applyItemOverrides = ({ fieldConfig, label, widthInColumns, isReadOnly = false }) => {
  const overriddenInputProps = {
    ...fieldConfig.inputProps,
    widthInColumns,
    ...getConditionalObject(isReadOnly, { disabled: true }),
  };

  if (label) {
    overriddenInputProps.label = label;
    overriddenInputProps.placeholder = label;
  }

  return {
    ...fieldConfig,
    inputProps: overriddenInputProps,
  };
};

const resolvePerRecordEntityConfigFields = ({
  entityKind,
  borrowerEntityId,
  borrowerDependentsContext,
  selectedContactId,
  contactTypesOptions,
}) => {
  if (entityKind === DrawerEntityKind.BORROWER) {
    return _.isNil(borrowerEntityId)
      ? null
      : getBorrowerConfigFieldsForEntity({
          entityId: borrowerEntityId,
          ...borrowerDependentsContext,
        });
  }

  return _.isNil(selectedContactId)
    ? null
    : getContactConfigFieldsForEntity({ contactId: selectedContactId, contactTypesOptions });
};

const SHARED_ENTITY_CONFIG_FIELDS_GETTER_BY_FORM_NAME = {
  [FormNames.CLOSING_INFORMATION]: ({ closingContext }) =>
    getClosingConfigFieldsForContext(closingContext),
  [FormNames.CLOSING_COMPANY_INFORMATION]: ({ closingContext }) =>
    getClosingCompanyConfigFieldsForContext(closingContext),
  [FormNames.FUNDING_INFORMATION]: ({ fundingContext }) =>
    getFundingConfigFieldsForContext(fundingContext),
};

const resolveSharedEntityBaseFieldConfig = ({
  entityKind,
  formName,
  fieldName,
  postClosingContext,
  mortgageInsuranceContext,
  appraisalContext,
  closingContext,
  fundingContext,
  loanAndLockConfigFields,
  hiddenPropertyFieldIds,
}) => {
  if (_.has(SHARED_ENTITY_CONFIG_FIELDS_GETTER_BY_FORM_NAME, formName)) {
    return SHARED_ENTITY_CONFIG_FIELDS_GETTER_BY_FORM_NAME[formName]({
      closingContext,
      fundingContext,
    })[fieldName];
  }

  if (formName === FormNames.APPRAISAL) {
    return getAppraisalConfigFieldsForContext(appraisalContext)[fieldName];
  }

  if (entityKind === DrawerEntityKind.POST_CLOSING) {
    return getPostClosingConfigFieldsForContext(postClosingContext)[fieldName];
  }

  if (entityKind === DrawerEntityKind.LOAN_AND_LOCK) {
    return resolveLoanAndLockBaseFieldConfig({
      formName,
      fieldName,
      mortgageInsuranceContext,
      loanAndLockConfigFields,
    });
  }

  if (entityKind === DrawerEntityKind.PROPERTY_INFO && hiddenPropertyFieldIds?.has(fieldName)) {
    return null;
  }

  return CONFIG_FIELDS_BY_ENTITY_KIND[entityKind][fieldName];
};

const checkIsClosingCompanySelectorField = ({ formName, fieldName }) =>
  formName === FormNames.CLOSING_COMPANY_INFORMATION &&
  fieldName === CLOSING_COMPANY_SELECTOR_FIELD_NAME;

const checkIsInapplicableToSelectedClosingCompany = ({ formName, closingContext }) =>
  formName === FormNames.CLOSING_COMPANY_INFORMATION &&
  checkIsEntityAvailable(closingContext) &&
  !_.isNil(closingContext.selectedCompanyPrefix);

const getLoanAndLockCascadeFieldPaths = ({ fieldName, escrowConfigInputs }) => [
  ...getConditionalArray(checkIsEscrowMasterFieldName(fieldName), () =>
    getEscrowCascadeFieldPaths(escrowConfigInputs),
  ),
  ...getDownPaymentCascadeFieldPaths(fieldName),
];

const checkRequiresClosingCompanySelection = ({ formName, closingContext }) =>
  formName === FormNames.CLOSING_COMPANY_INFORMATION &&
  checkIsEntityAvailable(closingContext) &&
  _.some(_.values(closingContext.companies)) &&
  _.isNil(closingContext.selectedCompanyPrefix);

const resolveLoanFieldItem = ({
  item,
  borrowerEntityId,
  borrowerDependentsContext,
  selectedContactId,
  contactTypesOptions,
  postClosingContext,
  mortgageInsuranceContext,
  appraisalContext,
  closingContext,
  fundingContext,
  loanAndLockConfigFields,
  escrowConfigInputs,
  hiddenPropertyFieldIds,
  readOnlyEntityKinds,
}) => {
  const parsed = parseLoanFieldKey(item?.loanFieldKey);
  const label = resolveLoanFieldLabel({ item, parsed });

  if (!parsed) {
    return {
      kind: DrawerItemResolutionKind.UNSUPPORTED,
      item,
      label,
    };
  }

  const entityKind = ENTITY_KIND_BY_FORM_NAME.get(parsed.form);

  if (_.isNil(entityKind)) {
    return {
      kind: DrawerItemResolutionKind.UNSUPPORTED,
      item,
      label,
    };
  }

  const isPerRecordEntity = PER_RECORD_ENTITY_KINDS.has(entityKind);

  if (checkIsClosingCompanySelectorField({ formName: parsed.form, fieldName: parsed.name })) {
    return {
      kind: DrawerItemResolutionKind.LOAN_FIELD,
      item,
      label,
      entityKind,
      formName: parsed.form,
      fieldName: parsed.name,
      fieldConfig: null,
      widthInColumns: resolveItemWidthInColumns(item),
    };
  }

  if (
    !checkIsFieldSupportedByEntity({ entityKind, formName: parsed.form, fieldName: parsed.name })
  ) {
    return {
      kind: DrawerItemResolutionKind.UNSUPPORTED,
      item,
      label,
    };
  }

  if (isPerRecordEntity) {
    const entityConfigFields = resolvePerRecordEntityConfigFields({
      entityKind,
      borrowerEntityId,
      borrowerDependentsContext,
      selectedContactId,
      contactTypesOptions,
    });
    const baseFieldConfig = entityConfigFields?.[parsed.name];

    if (_.isNil(baseFieldConfig)) {
      return {
        kind: DrawerItemResolutionKind.LOAN_FIELD,
        item,
        label,
        entityKind,
        formName: parsed.form,
        fieldName: parsed.name,
        fieldConfig: null,
        widthInColumns: resolveItemWidthInColumns(item),
        ...(entityKind === DrawerEntityKind.BORROWER
          ? { requiresBorrowerSelection: true }
          : { requiresContactSelection: true }),
      };
    }

    const widthInColumns = resolveItemWidthInColumns(item);

    return {
      kind: DrawerItemResolutionKind.LOAN_FIELD,
      item,
      label,
      entityKind,
      formName: parsed.form,
      fieldName: parsed.name,
      ...resolveItemFieldConfigs({
        baseFieldConfig,
        label: item?.labelOverride?.trim(),
        widthInColumns,
        isReadOnly: !!readOnlyEntityKinds?.has(entityKind),
      }),
      widthInColumns,
    };
  }

  const baseFieldConfig = resolveSharedEntityBaseFieldConfig({
    entityKind,
    formName: parsed.form,
    fieldName: parsed.name,
    postClosingContext,
    mortgageInsuranceContext,
    appraisalContext,
    closingContext,
    fundingContext,
    loanAndLockConfigFields,
    hiddenPropertyFieldIds,
  });

  if (
    _.isNil(baseFieldConfig) &&
    checkIsInapplicableToSelectedClosingCompany({ formName: parsed.form, closingContext })
  ) {
    return {
      kind: DrawerItemResolutionKind.INAPPLICABLE,
      item,
      label,
    };
  }

  if (_.isNil(baseFieldConfig)) {
    return {
      kind: DrawerItemResolutionKind.LOAN_FIELD,
      item,
      label,
      entityKind,
      formName: parsed.form,
      fieldName: parsed.name,
      fieldConfig: null,
      widthInColumns: resolveItemWidthInColumns(item),
      requiresClosingCompanySelection: checkRequiresClosingCompanySelection({
        formName: parsed.form,
        closingContext,
      }),
    };
  }

  const widthInColumns = resolveItemWidthInColumns(item);

  return {
    kind: DrawerItemResolutionKind.LOAN_FIELD,
    item,
    label,
    entityKind,
    formName: parsed.form,
    fieldName: parsed.name,
    ...resolveItemFieldConfigs({
      baseFieldConfig,
      label: CATALOG_LABEL_FORM_NAMES.has(parsed.form) ? label : item?.labelOverride?.trim(),
      widthInColumns,
      isReadOnly: !!readOnlyEntityKinds?.has(entityKind),
    }),
    widthInColumns,
    ...getConditionalObject(entityKind === DrawerEntityKind.LOAN_AND_LOCK, () => ({
      cascadeFieldPaths: getLoanAndLockCascadeFieldPaths({
        fieldName: parsed.name,
        escrowConfigInputs,
      }),
    })),
  };
};

const resolveCustomFieldItem = ({ item, customFieldDefinitionsById }) => {
  const customFieldDefinitionId = item?.customFieldDefinitionId ?? item?.customFieldId ?? null;
  const definition = customFieldDefinitionId
    ? customFieldDefinitionsById?.[customFieldDefinitionId]
    : null;
  const label = resolveCustomFieldLabel({ item, definition });

  if (_.isNil(definition)) {
    return {
      kind: DrawerItemResolutionKind.UNSUPPORTED,
      item,
      label,
    };
  }

  const isDisplayableApplicationField =
    definition.target === CUSTOM_FIELDS_TARGET.LOAN &&
    !!definition.isEnabled &&
    (isManualEnabled(definition) || isCalculatedDisplayableInLoanEditor(definition));

  if (!isDisplayableApplicationField) {
    return {
      kind: DrawerItemResolutionKind.UNSUPPORTED,
      item,
      label,
    };
  }

  return {
    kind: DrawerItemResolutionKind.CUSTOM_FIELD,
    item,
    label,
    customFieldDefinitionId,
    definition,
    widthInColumns: resolveItemWidthInColumns(item),
  };
};

const resolveDrawerItem = ({ item, customFieldDefinitionsById, ...loanFieldContext }) => {
  if (item?.kind === LoanEditorCustomFormItemKind.LOAN_FIELD) {
    return resolveLoanFieldItem({ item, ...loanFieldContext });
  }

  if (item?.kind === LoanEditorCustomFormItemKind.CUSTOM_FIELD) {
    return resolveCustomFieldItem({ item, customFieldDefinitionsById });
  }

  return {
    kind: DrawerItemResolutionKind.UNSUPPORTED,
    item,
    label: item?.labelOverride?.trim() || DRAWER_MESSAGES.UNSUPPORTED_FALLBACK_LABEL,
  };
};

export const getColumnCountForLayout = layout =>
  COLUMN_COUNT_BY_LAYOUT[layout] ?? DEFAULT_COLUMN_COUNT;

const overlayAutoLockOnFieldConfig = ({ fieldConfig, itemId, autoLockState }) => {
  if (!autoLockState || _.isNil(itemId)) {
    return fieldConfig;
  }

  const isDisabledByAutoLock = !!autoLockState.lockedItemIds?.[itemId];

  if (!isDisabledByAutoLock) {
    return fieldConfig;
  }

  return {
    ...fieldConfig,
    inputProps: {
      ...fieldConfig.inputProps,
      disabled: true,
    },
  };
};

const getResolutionFieldConfigs = ({ fieldConfig, fieldConfigs }) =>
  fieldConfigs ?? getConditionalArray(!_.isNil(fieldConfig), [fieldConfig]);

const buildLoanFieldGroups = ({ loanFieldResolutions, autoLockState }) => {
  const groupsByKey = loanFieldResolutions.reduce((acc, resolution) => {
    const { entityKind, formName, item } = resolution;
    const resolutionFieldConfigs = getResolutionFieldConfigs(resolution);

    if (!resolutionFieldConfigs.length) {
      return acc;
    }

    const groupKey = `${entityKind}::${formName}`;
    const group = acc[groupKey] ?? { entityKind, formName, items: [], fieldConfigs: [] };

    group.items.push(item);
    group.fieldConfigs.push(
      ...resolutionFieldConfigs.map(fieldConfig =>
        overlayAutoLockOnFieldConfig({ fieldConfig, itemId: item?.id, autoLockState }),
      ),
    );
    acc[groupKey] = group;

    return acc;
  }, {});

  return Object.values(groupsByKey).map(
    ({ entityKind, formName, items: groupItems, fieldConfigs }) => ({
      entityKind,
      formName,
      items: groupItems,
      config: fillConfigFieldsWithKeys({
        groups: [
          {
            type: FieldType.GROUP,
            key: `loan-editor-custom-form-${entityKind}-${formName}`,
            fields: fieldConfigs,
          },
        ],
      }),
    }),
  );
};

const buildEmptyFieldPathsByEntity = () =>
  Object.values(DrawerEntityKind).reduce((acc, entityKind) => {
    acc[entityKind] = new Set();

    return acc;
  }, {});

const collectFieldPathsByEntity = loanFieldResolutions =>
  loanFieldResolutions.reduce((acc, resolution) => {
    [
      ..._.map(getResolutionFieldConfigs(resolution), 'nameInModel'),
      ...(resolution.cascadeFieldPaths ?? []),
    ]
      .filter(path => _.isString(path) && path.length)
      .forEach(path => acc[resolution.entityKind].add(path));

    return acc;
  }, buildEmptyFieldPathsByEntity());

const filterByAutoLock = ({ resolutions, autoLockState }) => {
  if (!autoLockState) {
    return resolutions;
  }

  return resolutions.filter(resolution => {
    const itemId = resolution?.item?.id;

    if (_.isNil(itemId)) {
      return true;
    }

    return !autoLockState.hiddenItemIds?.[itemId];
  });
};

const resolveIframeHeightPx = iframeHeightPx => {
  const numeric = Number(iframeHeightPx);

  if (!Number.isFinite(numeric) || numeric <= 0) {
    return LOAN_EDITOR_CUSTOM_FORM_IFRAME_DEFAULT_HEIGHT_PX;
  }

  return Math.round(numeric);
};

const buildIframeSectionResolution = ({ iframeUrl, iframeHeightPx }) => ({
  kind: LoanEditorCustomFormSectionKind.IFRAME,
  iframeConfig: {
    iframeUrl: iframeUrl ?? '',
    iframeHeightPx: resolveIframeHeightPx(iframeHeightPx),
  },
  loanFieldGroups: [],
  fieldPathsByEntity: buildEmptyFieldPathsByEntity(),
  customFieldResolutions: [],
  unsupportedResolutions: [],
  inapplicableResolutions: [],
  requiresBorrowerSelection: false,
  requiresContactSelection: false,
  requiresClosingCompanySelection: false,
  hasBorrowerFields: false,
  hasContactFields: false,
  hasClosingCompanyFields: false,
});

export const buildSectionResolutions = ({
  kind,
  iframeUrl,
  iframeHeightPx,
  items,
  customFieldDefinitionsById,
  borrowerEntityId,
  borrowerDependentsContext,
  selectedContactId,
  contactTypesOptions,
  postClosingContext,
  mortgageInsuranceContext,
  appraisalContext,
  closingContext,
  fundingContext,
  loanAndLockContext,
  propertyPurposeContext,
  readOnlyEntityKinds,
  autoLockState,
}) => {
  if (kind === LoanEditorCustomFormSectionKind.IFRAME) {
    return buildIframeSectionResolution({ iframeUrl, iframeHeightPx });
  }

  const loanAndLockConfigFields = getLoanAndLockConfigFieldsForContext(loanAndLockContext);
  const hiddenPropertyFieldIds = getPurposeHiddenPropertyFieldIds(propertyPurposeContext);
  const resolutions = (items ?? []).map(item =>
    resolveDrawerItem({
      item,
      customFieldDefinitionsById,
      borrowerEntityId,
      borrowerDependentsContext,
      selectedContactId,
      contactTypesOptions,
      postClosingContext,
      mortgageInsuranceContext,
      appraisalContext,
      closingContext,
      fundingContext,
      loanAndLockConfigFields,
      escrowConfigInputs: loanAndLockContext?.escrowConfigInputs,
      hiddenPropertyFieldIds,
      readOnlyEntityKinds,
    }),
  );

  const visibleResolutions = filterByAutoLock({ resolutions, autoLockState });

  const loanFieldResolutions = visibleResolutions.filter(
    resolution => resolution.kind === DrawerItemResolutionKind.LOAN_FIELD,
  );
  const customFieldResolutions = visibleResolutions.filter(
    resolution => resolution.kind === DrawerItemResolutionKind.CUSTOM_FIELD,
  );
  const unsupportedResolutions = visibleResolutions.filter(
    resolution => resolution.kind === DrawerItemResolutionKind.UNSUPPORTED,
  );
  const inapplicableResolutions = visibleResolutions.filter(
    resolution => resolution.kind === DrawerItemResolutionKind.INAPPLICABLE,
  );

  const requiresBorrowerSelection = loanFieldResolutions.some(
    resolution => resolution.requiresBorrowerSelection,
  );

  const requiresContactSelection = loanFieldResolutions.some(
    resolution => resolution.requiresContactSelection,
  );

  const hasBorrowerFields = loanFieldResolutions.some(
    resolution => resolution.entityKind === DrawerEntityKind.BORROWER,
  );

  const hasContactFields = loanFieldResolutions.some(
    resolution => resolution.entityKind === DrawerEntityKind.CONTACT,
  );

  const requiresClosingCompanySelection = loanFieldResolutions.some(
    resolution => resolution.requiresClosingCompanySelection,
  );

  const hasClosingCompanyFields =
    checkIsEntityAvailable(closingContext) &&
    loanFieldResolutions.some(
      resolution => resolution.formName === FormNames.CLOSING_COMPANY_INFORMATION,
    );

  return {
    kind: LoanEditorCustomFormSectionKind.FIELDS,
    loanFieldGroups: buildLoanFieldGroups({ loanFieldResolutions, autoLockState }),
    fieldPathsByEntity: collectFieldPathsByEntity(loanFieldResolutions),
    customFieldResolutions: customFieldResolutions.map(resolution => ({
      ...resolution,
      disabled:
        !!autoLockState?.lockedItemIds?.[resolution?.item?.id] ||
        !!readOnlyEntityKinds?.has(DrawerEntityKind.CUSTOM_FIELD),
    })),
    unsupportedResolutions,
    inapplicableResolutions,
    requiresBorrowerSelection,
    requiresContactSelection,
    requiresClosingCompanySelection,
    hasBorrowerFields,
    hasContactFields,
    hasClosingCompanyFields,
  };
};

export const focusFirstInvalidField = container => {
  const invalidField = container?.querySelector(INVALID_FIELD_SELECTOR);

  if (!invalidField) {
    return;
  }

  invalidField.scrollIntoView({ block: 'center', behavior: 'smooth' });

  // The date picker writes a default date into an empty value on focus, so it only gets scrolled to.
  if (!invalidField.closest(DATE_PICKER_SELECTOR)) {
    invalidField.focus({ preventScroll: true });
  }
};

export const checkHasRenderableSectionContent = ({
  kind,
  loanFieldGroups,
  customFieldResolutions,
}) =>
  kind === LoanEditorCustomFormSectionKind.IFRAME ||
  !!loanFieldGroups?.length ||
  !!customFieldResolutions?.length;

export const buildReadOnlyEntityKinds = ({ isLoanEditorEditAllowed, isContactsEditAllowed }) =>
  new Set([
    ...getConditionalArray(!isLoanEditorEditAllowed, [
      DrawerEntityKind.LOAN_AND_LOCK,
      DrawerEntityKind.PROPERTY_INFO,
      DrawerEntityKind.BORROWER,
    ]),
    ...getConditionalArray(!isContactsEditAllowed, [DrawerEntityKind.CONTACT]),
  ]);

const toNoticeEntries = resolutions =>
  (resolutions ?? []).map(({ item, label }) => ({
    key: item?.id ?? item?.loanFieldKey ?? item?.customFieldDefinitionId ?? label,
    label,
  }));

export const buildUnsupportedEntries = sectionResolutions =>
  sectionResolutions.flatMap(({ unsupportedResolutions }) =>
    toNoticeEntries(unsupportedResolutions),
  );

export const buildInapplicableEntries = sectionResolutions =>
  sectionResolutions.flatMap(({ inapplicableResolutions }) =>
    toNoticeEntries(inapplicableResolutions),
  );

export const buildCustomFieldDefinitionsById = definitions =>
  (definitions ?? []).reduce((acc, definition) => {
    if (definition?.id) {
      acc[definition.id] = definition;
    }

    return acc;
  }, {});

export const buildLoanAndLockCanonicalConfig = ({
  loanAndLockContext,
  loanFieldGroups,
  mortgageInsuranceContext,
}) => {
  const mortgageInsuranceGroups = checkShouldSeedMortgageInsurance({
    loanFieldGroups,
    mortgageInsuranceContext,
  })
    ? getMortgageInsuranceGroups(mortgageInsuranceContext)
    : [];
  const isMortgageInsuranceOnly = checkIsMortgageInsuranceOnly(
    getPlacedFormNamesForEntity({
      loanFieldGroups: loanFieldGroups ?? [],
      entityKind: DrawerEntityKind.LOAN_AND_LOCK,
    }),
  );

  if (isMortgageInsuranceOnly) {
    return { groups: mortgageInsuranceGroups };
  }

  return {
    groups: [...getLoanInfoGroups(loanAndLockContext), ...mortgageInsuranceGroups],
  };
};

const buildSeedGroup = ({ key, fields }) =>
  fillConfigFieldsWithKeys({
    groups: [
      {
        type: FieldType.GROUP,
        key,
        fields,
      },
    ],
  }).groups[0];

const getPlacedFormNamesForEntity = ({ loanFieldGroups, entityKind }) =>
  loanFieldGroups.reduce((acc, group) => {
    if (group.entityKind === entityKind) {
      acc.add(group.formName);
    }

    return acc;
  }, new Set());

export const buildPropertyInfoCanonicalConfig = ({
  loanFieldGroups,
  propertyApplicationData,
  appraisalContext,
}) => {
  const placedFormNames = getPlacedFormNamesForEntity({
    loanFieldGroups,
    entityKind: DrawerEntityKind.PROPERTY_INFO,
  });

  const groups = [...placedFormNames].reduce((acc, formName) => {
    const getSeedFields = PROPERTY_SEED_FIELDS_BY_FORM_NAME[formName];

    if (getSeedFields) {
      acc.push(
        buildSeedGroup({
          key: `loan-editor-custom-form-property-info-${formName}-seed`,
          fields: getSeedFields({
            county: propertyApplicationData?.county ?? null,
            appraisalContext,
          }),
        }),
      );
    }

    return acc;
  }, []);

  return { groups };
};

export const buildBorrowerCanonicalConfig = ({
  loanFieldGroups,
  borrowerEntityId,
  borrowerDependentsContext,
}) => {
  const hasBorrowerGroups = loanFieldGroups.some(
    group => group.entityKind === DrawerEntityKind.BORROWER,
  );

  if (!hasBorrowerGroups || _.isNil(borrowerEntityId)) {
    return { groups: [] };
  }

  const fields = Object.values(
    getBorrowerConfigFieldsForEntity({ entityId: borrowerEntityId, ...borrowerDependentsContext }),
  ).flat();

  return {
    groups: [buildSeedGroup({ key: 'loan-editor-custom-form-borrower-seed', fields })],
  };
};

export const collectPlacedFieldPathsByEntity = sectionResolutions =>
  sectionResolutions.reduce((acc, { fieldPathsByEntity }) => {
    Object.values(DrawerEntityKind).forEach(entityKind => {
      fieldPathsByEntity?.[entityKind]?.forEach(path => acc[entityKind].add(path));
    });

    return acc;
  }, buildEmptyFieldPathsByEntity());

export const collectPlacedPostClosingSectionKeys = postClosingFieldPaths =>
  [...(postClosingFieldPaths ?? [])].reduce((acc, path) => {
    const sectionKey =
      POST_CLOSING_SECTION_KEY_BY_FIELD_NAME[path.slice(POST_CLOSING_FORM_PATH.length + 1)];

    if (sectionKey) {
      acc.add(sectionKey);
    }

    return acc;
  }, new Set());

export const buildPostClosingCanonicalConfig = ({ postClosingFieldPaths, postClosingContext }) => ({
  groups: [...collectPlacedPostClosingSectionKeys(postClosingFieldPaths)].map(sectionKey =>
    buildSeedGroup({
      key: `loan-editor-custom-form-post-closing-${sectionKey}-seed`,
      fields: getDrawerPostClosingSectionFields({ sectionKey, postClosingContext }),
    }),
  ),
});

const checkHasItemsOfForms = ({ sections, formNames }) =>
  (sections ?? []).some(section =>
    (section?.items ?? []).some(
      item =>
        item?.kind === LoanEditorCustomFormItemKind.LOAN_FIELD &&
        formNames.has(parseLoanFieldKey(item.loanFieldKey)?.form),
    ),
  );

export const checkHasPostClosingItems = sections =>
  checkHasItemsOfForms({ sections, formNames: SUPPORTED_POST_CLOSING_FORM_NAMES });

export const checkHasAppraisalItems = sections =>
  checkHasItemsOfForms({ sections, formNames: APPRAISAL_FORM_NAMES });

export const checkHasClosingItems = sections =>
  checkHasItemsOfForms({ sections, formNames: SUPPORTED_CLOSING_FORM_NAMES });

export const checkHasFundingItems = sections =>
  checkHasItemsOfForms({ sections, formNames: SUPPORTED_FUNDING_FORM_NAMES });

const buildEntitySeedConfig = ({ loanFieldGroups, entityKind, key, getSeedFields }) =>
  loanFieldGroups.some(group => group.entityKind === entityKind)
    ? { groups: [buildSeedGroup({ key, fields: getSeedFields() })] }
    : { groups: [] };

export const buildClosingCanonicalConfig = ({ loanFieldGroups, closingContext }) =>
  buildEntitySeedConfig({
    loanFieldGroups,
    entityKind: DrawerEntityKind.CLOSING,
    key: 'loan-editor-custom-form-closing-seed',
    getSeedFields: () => getClosingSeedFields(closingContext),
  });

export const buildFundingCanonicalConfig = ({ loanFieldGroups, fundingContext }) =>
  buildEntitySeedConfig({
    loanFieldGroups,
    entityKind: DrawerEntityKind.FUNDING,
    key: 'loan-editor-custom-form-funding-seed',
    getSeedFields: () => getFundingSeedFields(fundingContext),
  });

export const buildContactCanonicalConfig = ({
  loanFieldGroups,
  selectedContactId,
  contactTypesOptions,
}) => {
  const hasContactGroups = loanFieldGroups.some(
    group => group.entityKind === DrawerEntityKind.CONTACT,
  );

  if (!hasContactGroups || _.isNil(selectedContactId)) {
    return { groups: [] };
  }

  return {
    groups: getContactConfigGroupsForEntity({ contactId: selectedContactId, contactTypesOptions }),
  };
};

export const buildPlacedFieldPaths = sectionResolutions => {
  const paths = new Set();

  sectionResolutions.forEach(({ fieldPathsByEntity }) => {
    Object.values(fieldPathsByEntity ?? {}).forEach(entityPaths =>
      entityPaths?.forEach(path => paths.add(path)),
    );
  });

  return paths;
};

export const buildPlacedCustomFieldDefinitionIds = sectionResolutions =>
  sectionResolutions.flatMap(({ customFieldResolutions }) =>
    customFieldResolutions
      .map(({ customFieldDefinitionId }) => customFieldDefinitionId)
      .filter(Boolean),
  );

export const checkHasSavableCustomFieldChanges = ({ dirtyValues, sectionResolutions }) => {
  const resolutions = sectionResolutions.flatMap(
    ({ customFieldResolutions }) => customFieldResolutions,
  );
  const lockedIds = new Set(_.map(_.filter(resolutions, 'disabled'), 'customFieldDefinitionId'));
  const dirtyIds = Object.keys(dirtyValues ?? {});

  return !!dirtyIds.length && !dirtyIds.some(definitionId => lockedIds.has(definitionId));
};

export const buildLoanAndLockEntitySideData = loanAndLockResponseData => {
  if (!loanAndLockResponseData) {
    return {};
  }

  return {
    lock: loanAndLockResponseData.lock ?? null,
    additionalMortgages: loanAndLockResponseData.additionalMortgages ?? [],
    loanTagAssignments: loanAndLockResponseData.loanTagAssignments ?? [],
    loanRoleAssignments: loanAndLockResponseData.loanRoleAssignments ?? [],
  };
};

export const buildPropertyEntitySideData = propertyApplicationData => ({
  propertyTaxes: propertyApplicationData.proposedPropertyExpenses?.propertyTaxes ?? [],
  supplementalInsurances:
    propertyApplicationData.proposedPropertyExpenses?.supplementalInsurances ?? [],
  otherExpenses: propertyApplicationData.proposedPropertyExpenses?.otherExpenses ?? [],
});

export const collectEditedFieldPaths = ({ formPendingChanges, fieldPaths }) =>
  new Set(
    [...(fieldPaths ?? [])].filter(path =>
      checkIsPendingChangeHasDifference(_.get(formPendingChanges, path)),
    ),
  );

export const hasEntityChanges = ({ formPendingChanges, fieldPaths }) =>
  !!collectEditedFieldPaths({ formPendingChanges, fieldPaths }).size;

const LOAN_EDITOR_CUSTOM_FORM_PREFIX = 'LoanEditorCustomForm:';

const CUSTOM_FORM_FIELD_KEY_PARTS = [
  'loanEditorCustomFormsSection',
  'loanEditorCustomFormsSubsection',
  'loanEditorCustomFormsTab',
];

export const buildCustomFormAutoLockState = ({ slug, hiddenFieldIds, lockedFieldIds }) => {
  if (!slug) {
    return null;
  }

  const fieldKeyPrefix = `${CUSTOM_FORM_FIELD_KEY_PARTS.join(
    '-',
  )}-${LOAN_EDITOR_CUSTOM_FORM_PREFIX}${slug}-`;

  const extractItemIds = fieldIds =>
    (fieldIds ?? []).reduce((acc, fieldId) => {
      if (!_.isString(fieldId)) {
        return acc;
      }

      if (!fieldId.startsWith(fieldKeyPrefix)) {
        return acc;
      }

      const itemId = fieldId.slice(fieldKeyPrefix.length);

      if (itemId) {
        acc[itemId] = true;
      }

      return acc;
    }, {});

  return {
    hiddenItemIds: extractItemIds(hiddenFieldIds),
    lockedItemIds: extractItemIds(lockedFieldIds),
  };
};
